import React, { useContext, useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { seedQuotes } from './seed-quotes';

const RANGE_OPTIONS = ['low', 'mid', 'high'] as const;

// Neuroticism is inverted: low score = calm/stable, high score = anxious/reactive
const NEUROTICISM_LABELS: Record<string, string> = {
  low: 'Low (0–33) — Calm, stable',
  mid: 'Mid (34–66) — Moderate',
  high: 'High (67–100) — Anxious, reactive',
};

const WELLBEING_LABELS: Record<string, string> = {
  low: 'Low (0–33) — Struggling',
  mid: 'Mid (34–66) — Moderate',
  high: 'High (67–100) — Thriving',
};

const SELF_EFFICACY_LABELS: Record<string, string> = {
  low: 'Low (0–33) — Low confidence',
  mid: 'Mid (34–66) — Moderate',
  high: 'High (67–100) — Confident, capable',
};

const DIMENSION_LABELS: Record<string, string> = {
  a: 'Neuroticism',
  b: 'Well-being',
  c: 'Self-efficacy',
};

const DIMENSION_RANGE_LABELS: Record<string, Record<string, string>> = {
  a: NEUROTICISM_LABELS,
  b: WELLBEING_LABELS,
  c: SELF_EFFICACY_LABELS,
};

interface QuoteDoc {
  _id: Id<"inner_weather_quotes">;
  quote: string;
  author: string;
  bucket_key: string;
  a_range: string;
  b_range: string;
  c_range: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const InnerWeatherQuotes: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const quotes = useQuery(api.inner_weather_quotes.list) || [];
  const counts = useQuery(api.inner_weather_quotes.getCounts);
  const createQuote = useMutation(api.inner_weather_quotes.create);
  const updateQuote = useMutation(api.inner_weather_quotes.update);
  const deleteQuote = useMutation(api.inner_weather_quotes.remove);
  const toggleActive = useMutation(api.inner_weather_quotes.toggleActive);
  const seedMutation = useMutation(api.inner_weather_quotes.seed);

  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; quote?: QuoteDoc }>({ open: false });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<Id<"inner_weather_quotes"> | null>(null);
  const [formData, setFormData] = useState({
    quote: '',
    author: '',
    a_range: 'mid' as string,
    b_range: 'mid' as string,
    c_range: 'mid' as string,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [filterBucket, setFilterBucket] = useState<string>('all');
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (editingId) {
      const quote = quotes.find(q => q._id === editingId);
      if (quote) {
        setFormData({
          quote: quote.quote,
          author: quote.author,
          a_range: quote.a_range,
          b_range: quote.b_range,
          c_range: quote.c_range,
        });
      }
    }
  }, [editingId, quotes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.quote.trim() || !formData.author.trim()) {
      setAlertMessage('Quote and author are required');
      setShowAlert(true);
      return;
    }

    try {
      if (editingId) {
        await updateQuote({
          id: editingId,
          quote: formData.quote,
          author: formData.author,
          a_range: formData.a_range,
          b_range: formData.b_range,
          c_range: formData.c_range,
          updated_by: convexUserId as Id<"users">,
        });
        setAlertMessage('Quote updated successfully!');
      } else {
        await createQuote({
          quote: formData.quote,
          author: formData.author,
          a_range: formData.a_range,
          b_range: formData.b_range,
          c_range: formData.c_range,
          created_by: convexUserId as Id<"users">,
        });
        setAlertMessage('Quote created successfully!');
      }
      setShowAlert(true);
      resetForm();
    } catch (error) {
      setAlertMessage('Error saving quote. Please try again.');
      setShowAlert(true);
    }
  };

  const resetForm = () => {
    setFormData({ quote: '', author: '', a_range: 'mid', b_range: 'mid', c_range: 'mid' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (quote: QuoteDoc) => {
    setEditingId(quote._id);
    setShowForm(true);
  };

  const handleToggleActive = async (id: Id<"inner_weather_quotes">) => {
    try {
      await toggleActive({ id, updated_by: convexUserId as Id<"users"> });
    } catch (error) {
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const count = await seedMutation({
        quotes: seedQuotes,
        created_by: convexUserId as Id<"users">,
      });
      setAlertMessage(`Seeded ${count} quotes successfully!`);
      setShowAlert(true);
    } catch (error) {
      setAlertMessage('Error seeding quotes. Please try again.');
      setShowAlert(true);
    } finally {
      setSeeding(false);
    }
  };

  // Build bucket filter options from data
  const allBucketKeys = [...new Set(quotes.map(q => q.bucket_key))].sort();
  const filteredQuotes = filterBucket === 'all' ? quotes : quotes.filter(q => q.bucket_key === filterBucket);

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={() => navigate('../')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-white border-2 border-primary/30">
                <span className="text-2xl">🌤️</span>
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-1">Inner Weather Quotes</h1>
                <p className="text-base text-muted-foreground">
                  Manage quotes for each combination of inner weather dimensions ({quotes.length} total)
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              {quotes.length === 0 && (
                <Button
                  color="secondary"
                  className="rounded-full"
                  onClick={handleSeed}
                  disabled={seeding}
                >
                  {seeding ? 'Seeding...' : 'Seed 135 Quotes'}
                </Button>
              )}
              <Button
                color="primary"
                className="rounded-full"
                onClick={() => { resetForm(); setShowForm(true); }}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Quote
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-6">
          {/* Bucket Coverage Summary */}
          {counts && (
            <div className="border-2 border-border/50 rounded-xl p-4 bg-card">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Coverage by Bucket (27 combos)</h3>
              <div className="flex flex-wrap gap-2">
                {RANGE_OPTIONS.map(a =>
                  RANGE_OPTIONS.map(b =>
                    RANGE_OPTIONS.map(c => {
                      const key = `${a}-${b}-${c}`;
                      const count = counts[key];
                      const activeCount = count?.active ?? 0;
                      return (
                        <button
                          key={key}
                          onClick={() => setFilterBucket(filterBucket === key ? 'all' : key)}
                          className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                            filterBucket === key
                              ? 'bg-primary text-white border-primary'
                              : activeCount > 0
                                ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                                : 'bg-red-50 border-red-200 text-red-500 hover:bg-red-100'
                          }`}
                        >
                          {key} ({activeCount})
                        </button>
                      );
                    })
                  )
                )}
              </div>
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 p-6 rounded-xl">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {editingId ? 'Edit Quote' : 'New Quote'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Field>
                  <Label>Quote</Label>
                  <Textarea
                    required
                    rows={3}
                    value={formData.quote}
                    onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                    placeholder="Enter the quote text..."
                  />
                </Field>

                <Field>
                  <Label>Author</Label>
                  <Input
                    required
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Quote author..."
                  />
                </Field>

                <div className="grid grid-cols-3 gap-4">
                  <Field>
                    <Label>{DIMENSION_LABELS.a}</Label>
                    <select
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      value={formData.a_range}
                      onChange={(e) => setFormData({ ...formData, a_range: e.target.value })}
                    >
                      {RANGE_OPTIONS.map(r => (
                        <option key={r} value={r}>{DIMENSION_RANGE_LABELS.a[r]}</option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    <Label>{DIMENSION_LABELS.b}</Label>
                    <select
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      value={formData.b_range}
                      onChange={(e) => setFormData({ ...formData, b_range: e.target.value })}
                    >
                      {RANGE_OPTIONS.map(r => (
                        <option key={r} value={r}>{DIMENSION_RANGE_LABELS.b[r]}</option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    <Label>{DIMENSION_LABELS.c}</Label>
                    <select
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      value={formData.c_range}
                      onChange={(e) => setFormData({ ...formData, c_range: e.target.value })}
                    >
                      {RANGE_OPTIONS.map(r => (
                        <option key={r} value={r}>{DIMENSION_RANGE_LABELS.c[r]}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <p className="text-xs text-muted-foreground">
                  Bucket: <span className="font-mono font-medium">{formData.a_range}-{formData.b_range}-{formData.c_range}</span>
                </p>

                <div className="flex gap-3">
                  <Button type="submit" color="primary" className="rounded-full">
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" color="secondary" className="rounded-full" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Filter */}
          {filterBucket !== 'all' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtered by:</span>
              <span className="text-sm font-mono font-medium bg-primary/10 px-2 py-1 rounded">{filterBucket}</span>
              <Button color="secondary" className="rounded-full text-xs" onClick={() => setFilterBucket('all')}>
                Clear
              </Button>
            </div>
          )}

          {/* Table */}
          <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
            <Table>
              <TableHead>
                <TableRow className="bg-muted/30">
                  <TableHeader>Bucket</TableHeader>
                  <TableHeader>Quote</TableHeader>
                  <TableHeader>Author</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuotes.map((quote) => (
                  <TableRow key={quote._id} className="hover:bg-muted/20">
                    <TableCell>
                      <span className="font-mono text-xs bg-muted/50 px-2 py-1 rounded">{quote.bucket_key}</span>
                    </TableCell>
                    <TableCell className="max-w-sm">
                      <p className="text-sm text-foreground truncate">{quote.quote}</p>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {quote.author}
                    </TableCell>
                    <TableCell>
                      {quote.is_active ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                          <CheckCircleIcon className="h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground bg-muted/30 border border-border/50 rounded-full">
                          <XCircleIcon className="h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button color="secondary" className="rounded-full" onClick={() => handleEdit(quote)}>
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          color={quote.is_active ? "secondary" : "primary"}
                          className="rounded-full"
                          onClick={() => handleToggleActive(quote._id)}
                        >
                          {quote.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          color="red"
                          className="rounded-full"
                          onClick={() => setConfirmDelete({ open: true, quote })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredQuotes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      {quotes.length === 0
                        ? 'No quotes found. Click "Seed 135 Quotes" to get started.'
                        : 'No quotes match the current filter.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={async () => {
          if (!confirmDelete.quote) return;
          try {
            await deleteQuote({ id: confirmDelete.quote._id });
            setConfirmDelete({ open: false });
          } catch (error) {
          }
        }}
        title="Delete Quote"
        description="Are you sure you want to delete this quote? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />

      <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowAlert(false)}>Ok</Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
